import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  FileCheck,
  LayoutDashboard,
  FileText,
  Upload,
  Settings,
  ListChecks,
  BarChart3,
  AlertTriangle,
  LogOut,
  User,
  ChevronDown,
  Menu,
  X,
  Layers,
  GitCompare,
  Wand2,
  Workflow,
  BookOpen,
} from 'lucide-react';
import { useState } from 'react';

const navigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  {
    name: 'Documents', icon: FileText, children: [
      { name: 'Documents', href: '/documents', icon: FileText },
      { name: 'Upload', href: '/upload', icon: Upload },
    ],
  },
  {
    name: 'Validation', icon: ListChecks, children: [
      { name: 'Validation Rules', href: '/validation-rules', icon: ListChecks },
      { name: 'Parser Templates', href: '/parser-templates', icon: Layers },
      { name: 'Reports', href: '/reports', icon: BarChart3 },
      { name: 'Issues', href: '/issues', icon: AlertTriangle },
    ],
  },
  {
    name: 'Data', icon: GitCompare, children: [
      { name: 'Data Matching', href: '/matching', icon: GitCompare },
      { name: 'Data Massaging', href: '/massaging', icon: Wand2 },
      { name: 'Pipelines', href: '/pipelines', icon: Workflow },
    ],
  },
  { name: 'Settings', href: '/settings', icon: Settings },
  { name: 'API Docs', href: `${process.env.REACT_APP_BACKEND_URL}/api/docs`, icon: BookOpen, external: true },
];

const isItemActive = (pathname, href) =>
  pathname === href || (href !== '/' && pathname.startsWith(href));

const navLinkClass = (active) =>
  `flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
    active ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
  }`;

const DesktopNavItem = ({ item, pathname }) => {
  if (item.external) {
    return (
      <a href={item.href} target="_blank" rel="noreferrer" className={navLinkClass(false)} data-testid="nav-api-docs-link">
        <item.icon className="w-4 h-4" />
        {item.name}
      </a>
    );
  }
  if (!item.children) {
    return (
      <Link to={item.href} className={navLinkClass(isItemActive(pathname, item.href))}
        data-testid={`nav-link-${item.name.toLowerCase().replace(/\s+/g, '-')}`}>
        <item.icon className="w-4 h-4" />
        {item.name}
      </Link>
    );
  }
  const groupActive = item.children.some((c) => isItemActive(pathname, c.href));
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className={navLinkClass(groupActive)}
          data-testid={`nav-group-${item.name.toLowerCase().replace(/\s+/g, '-')}`}>
          <item.icon className="w-4 h-4" />
          {item.name}
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-52">
        {item.children.map((c) => (
          <DropdownMenuItem key={c.name} asChild>
            <Link to={c.href}
              className={`cursor-pointer ${isItemActive(pathname, c.href) ? 'font-semibold text-primary' : ''}`}
              data-testid={`nav-link-${c.name.toLowerCase().replace(/\s+/g, '-')}`}>
              <c.icon className="w-4 h-4 mr-2" />
              {c.name}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const mobileLinkClass = (active) =>
    `flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-md transition-colors ${
      active ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
    }`;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/80 backdrop-blur-xl">
        <div className="flex h-16 items-center justify-between px-4 md:px-6">
          {/* Logo & Nav */}
          <div className="flex items-center gap-8">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
                <FileCheck className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="text-xl font-bold tracking-tight font-['Manrope'] hidden sm:block">Data Validation</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navigation.map((item) => (
                <DesktopNavItem key={item.name} item={item} pathname={location.pathname} />
              ))}
            </nav>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-4">
            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2" data-testid="user-menu-button">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-4 h-4" />
                  </div>
                  <span className="hidden sm:block text-sm font-medium">{user?.name}</span>
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="cursor-pointer">
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive cursor-pointer" data-testid="logout-button">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t bg-background p-4 max-h-[70vh] overflow-y-auto">
            <nav className="flex flex-col gap-1">
              {navigation.map((item) => {
                if (item.external) {
                  return (
                    <a key={item.name} href={item.href} target="_blank" rel="noreferrer"
                      onClick={() => setMobileMenuOpen(false)}
                      className={mobileLinkClass(false)} data-testid="nav-api-docs-link-mobile">
                      <item.icon className="w-5 h-5" />
                      {item.name}
                    </a>
                  );
                }
                if (!item.children) {
                  return (
                    <Link key={item.name} to={item.href} onClick={() => setMobileMenuOpen(false)}
                      className={mobileLinkClass(isItemActive(location.pathname, item.href))}>
                      <item.icon className="w-5 h-5" />
                      {item.name}
                    </Link>
                  );
                }
                return (
                  <div key={item.name} className="space-y-1">
                    <p className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{item.name}</p>
                    {item.children.map((c) => (
                      <Link key={c.name} to={c.href} onClick={() => setMobileMenuOpen(false)}
                        className={`${mobileLinkClass(isItemActive(location.pathname, c.href))} pl-6`}>
                        <c.icon className="w-5 h-5" />
                        {c.name}
                      </Link>
                    ))}
                  </div>
                );
              })}
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 md:px-6 py-8">
        <Outlet />
      </main>
    </div>
  );
}
